import React, { useState, useEffect, useMemo } from "react";
import styles from "./Footer.module.scss";

import { Link } from "react-router-dom";

import BGPanel from "../../assets/images/svg/footer-bg.svg?react";
import FooterAmp from "../../assets/images/svg/footer-amp.svg?react";
import FooterRocket from "../../assets/images/svg/footer-rocket.svg?react";
import FooterAmpMask from "../../assets/images/svg/footer-amp-mask.svg?react";
import FooterMeteors from "../../assets/images/svg/footer-meteors.svg?react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { faFacebookSquare } from "@fortawesome/free-brands-svg-icons";
import { useLocale, useLocaleRouteMatch } from "../../i18n/LocaleContext";
import { featureFlags } from "../../utilities/featureFlags";

const Footer = () => {
  const [showRocket, setShowRocket] = useState(false);
  const { t, localePath } = useLocale();

  const footerRef = React.createRef();

  const footerObserver = useMemo(
    () =>
      new IntersectionObserver(onFooterIntersection, {
        rootMargin: "0px 0px",
        threshold: 0.5,
      })
  );

  useEffect(() => {
    if (window && "IntersectionObserver" in window) {
      if (footerRef && footerRef.current) {
        footerObserver.observe(footerRef.current);
      }
    } else {
      setShowRocket(true);
    }
    return () => {
      footerObserver.disconnect();
    };
  }, [setShowRocket]);

  function onFooterIntersection(entries) {
    if (!entries || entries.length <= 0) {
      return;
    }

    if (entries[0].isIntersecting) {
      setShowRocket(true);
    } else if (showRocket) {
      setShowRocket(false);
    }
  }

  // Check current route using shared hook (avoids useMatch hook count issues)
  const routeMatches = useLocaleRouteMatch();
  const isHome = routeMatches("/");
  const isAbout = routeMatches("/about");
  const isStore = routeMatches("/shop");

  const footerLinks = [];

  if (!isHome) {
    footerLinks.push({ to: localePath("/"), label: t("nav.home") });
  }

  if (featureFlags.shop && !isStore) {
    footerLinks.push({ to: localePath("/shop"), label: t("nav.store") });
  }

  if (!isAbout) {
    footerLinks.push({ to: localePath("/about"), label: t("nav.aboutUs") });
  }

  return (
    <React.Fragment>
      <div className={styles.FooterPaddingTop} />
      <div className={styles.Footer}>
        <div className={styles.FooterBG} ref={footerRef}>
          <FooterAmp className={styles.Ampersand} />
          <BGPanel className={styles.BGPanel} />
          <FooterRocket
            className={`${styles.Rocket} ${
              showRocket ? styles.RocketShowing : styles.RocketHidden
            }`}
          />
          <FooterAmpMask className={styles.FooterAmpMask} />

          <div className={styles.FooterContent}>
            <h2 className={styles.ConnectHeading}>{t("footer.connectWithUs")}</h2>
            <div className={styles.SocialIcons}>
              <a
                href="mailto:info@universeandmore.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("social.email")}
                className={styles.SocialLink}
              >
                <FontAwesomeIcon
                  icon={faPaperPlane}
                  className={styles.SocialIcon}
                />
              </a>
              <a
                href="https://www.facebook.com/universeandmore"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("social.facebook")}
                className={styles.SocialLink}
              >
                <FontAwesomeIcon
                  className={styles.SocialIcon}
                  icon={faFacebookSquare}
                />
              </a>
            </div>
            <div className={styles.FooterLinks}>
              {footerLinks.map((link, index) => (
                <React.Fragment key={link.to}>
                  {index > 0 && " | "}
                  <Link to={link.to}>
                    <p className={styles.FooterLink}>{link.label}</p>
                  </Link>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.Copyright}>
          <p>
            {t("footer.websiteBy")} &copy; {new Date().getFullYear()}
            <span className={styles.Copyright2}>
              {t("footer.nonprofit")} -
              <a
                className={styles.Terms}
                href="https://www.termsfeed.com/live/cd05b5f9-40f2-476d-bc6b-02dde767fed9"
                target="_blank"
              >
                {t("footer.termsConditions")}
              </a>
            </span>
          </p>
        </div>
        <FooterMeteors className={styles.Meteors} />
      </div>
    </React.Fragment>
  );
};

export default Footer;
